import CategoryIcon from '@mui/icons-material/Category';
import HelpIcon from '@mui/icons-material/Help';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { blueGrey, grey, red } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AlertIcon from '../../shared-components/Icons/AlertIcon';
import DataInsightIcon from '../../shared-components/Icons/DataInsightIcon';
import EncouragementIcon from '../../shared-components/Icons/EncouragementIcon';
import UpdateIcon from '../../shared-components/Icons/UpdateIcon';
import { INotation } from '../../shared-interfaces/Notation/INotation';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../../shared-interfaces/User/ICoreUser';
import NotationService from '../../shared-services/Notation/NotationService';
import { NotationType } from '../../shared-types/Notation/NotationType';
import ResponseContainer from './Components/ResponseContainer';

interface INotationDetailsHistoryState {
  notation: INotation;
}

interface INotationDetailsParams {
  notationType: string | undefined;
  id: number | undefined;
}

const useStyles = makeStyles(theme => ({
  avatar: {
    width: '80px',
    height: '80px',
    [theme.breakpoints.down('md')]: {
      width: '48px',
      height: '48px',
    },
  },
  badge: {
    top: '8px',
    right: '2px',
  },
  listItem: {
    borderBottom: '1px solid silver',
  },
  listItemText: {
    marginLeft: '16px',
  },
  titleText: {
    marginBottom: '0px',
  },
  heading: {
    fontWeight: 600,
  },
  listHeading: {
    fontWeight: 600,
  },
  caption: {
    fontSize: '0.85rem',
    fontStyle: 'italic',
    color: '#666',
  },
  content: {
    backgroundColor: 'white',
    padding: '30px',
    boxShadow: '2px 2px 0 rgba(0,0,0,.1)',
    borderLeft: `1px solid ${grey[300]}`,
    borderTop: `1px solid ${grey[300]}`,
    [theme.breakpoints.down('md')]: {
      padding: '4px',
    },
  },
  openAlertChip: {
    marginRight: '4px',
    color: red[700],
    backgroundColor: '#fff',
    border: `1px solid ${red[700]}`,
    '& svg': {
      color: red[700],
    },
  },
  assignmentChip: {
    marginRight: '4px',
    backgroundColor: blueGrey[600],
    border: '1px sold black',
    borderRadius: '16px',
    color: 'white',
  },
  categoryChip: {
    border: 'none',
    marginRight: '4px',
    backgroundColor: '#fff',
    color: grey[900],
  },
  detailContainer: {
    '& p': {
      margin: 0,
    },
    marginBottom: '8px',
  },
  detailContainerBody: {
    [theme.breakpoints.down('md')]: {
      padding: '16px',
    },
    padding: '0 16px 0 112px',
  },
}));

const NotationDetailsLoadingSkeleton = () => {
  const classes = useStyles();
  return (
    <ListItem ContainerComponent="div">
      <ListItemAvatar>
        <Skeleton variant="circular" className={classes.avatar} />
      </ListItemAvatar>
      <ListItemText
        className={classes.listItemText}
        disableTypography
        primary={<Skeleton variant="text" width="350px" height="30px" />}
        secondary={<Skeleton variant="text" width="500px" height="20px" />}
      />
    </ListItem>
  );
};

const AdditionalAudienceList = (props: { additionalAudience: ICoreUser[] }) => {
  const mappedNames = props.additionalAudience.map(u => {
    return GetCoreUserFormattedName(u, false);
  });

  return (
    <>
      {props.additionalAudience.length > 10 ? (
        <>
          {mappedNames.slice(0, 3).join(', ')}{' '}
          <Tooltip title={mappedNames.join(', ')} arrow>
            <span>+ {mappedNames.length - 10} more</span>
          </Tooltip>
        </>
      ) : (
        <>{mappedNames.join(', ')}</>
      )}
    </>
  );
};

function audienceMapper(audienceType: string) {
  switch (audienceType) {
    case 'All':
      return 'Professional Staff';
    case 'FullNetwork':
      return 'Full Network';
    case 'Private':
      return 'Private';
    case 'SpecificNetworkMembers':
      return 'Specific Network Members';
    default:
      return audienceType;
  }
}

const NotationDetails = () => {
  const classes = useStyles();
  const location = useLocation();

  //@ts-ignore
  const { notationType, id } = useParams() as INotationDetailsParams;

  const [notation, setNotation] = useState<INotation>({} as INotation);
  const [initialLoading, setInitialLoading] = useState(true);

  const [additionalAudience, setAdditionalAudience] = useState<ICoreUser[]>([]);
  const [alertStatus, setAlertStatus] = useState<'Open' | 'Lowered' | null>(
    null,
  );
  const [assignedTo, setAssignedTo] = useState<ICoreUser>();

  const canHaveComments =
    ['alert', 'update', 'datainsight'].findIndex(
      x => x === notationType?.toLowerCase(),
    ) !== -1;

  useEffect(() => {
    if (location.state && location.state.notation) {
      setNotation(location.state.notation);
      setAlertStatus(location.state.notation.alertStatus);
      setAssignedTo(location.state.notation.assignedTo);
      setInitialLoading(false);
    } else if (notationType && id) {
      NotationService.getNotation(notationType, id)
        .then(n => {
          setNotation(n);
          setAlertStatus(n.alertStatus);
          setAssignedTo(n.assignedTo);
        })
        .finally(() => {
          setInitialLoading(false);
        });
    } else {
      // Error state
    }
  }, []);

  useEffect(() => {
    if (notation.id) {
      var audience = notation.advisorConnections
        .filter(x => x.connectionType === 'AdditionalAudienceMember')
        .map(x => x.account);

      setAdditionalAudience(audience);
    }
  }, [notation]);

  const ChipContainer = () => {
    return (
      <>
        {notation.categories &&
          notation.categories.map((category, i) => {
            return (
              <Chip
                key={i}
                variant="outlined"
                label={category.name}
                avatar={<CategoryIcon style={{ color: grey[900] }} />}
                color="default"
                size="small"
                className={classes.categoryChip}
              />
            );
          })}
      </>
    );
  };

  return (
    <>
      <Typography variant="h1">Notation Details</Typography>
      <Paper className={classes.content}>
        <Grid container>
          <Grid item xs={12}>
            <List>
              {!initialLoading ? (
                <>
                  <ListItem ContainerComponent="div">
                    <ListItemAvatar>
                      <Badge
                        badgeContent={
                          notation.notationType === NotationType.Alert ? (
                            <AlertIcon />
                          ) : notation.notationType ===
                            NotationType.Encouragement ? (
                            <EncouragementIcon />
                          ) : notation.notationType === NotationType.Update ? (
                            <UpdateIcon />
                          ) : notation.notationType ===
                            NotationType.DataInsight ? (
                            <DataInsightIcon />
                          ) : (
                            <HelpIcon />
                          )
                        }
                        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                        className={classes.badge}
                      >
                        <Avatar
                          className={classes.avatar}
                          alt={GetCoreUserFormattedName(
                            notation.student,
                            false,
                          )}
                          src={
                            notation.student?.profileImageFilePath ??
                            notation.student?.institutionProfileImageFilePath
                          }
                        />
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      className={classes.listItemText}
                      disableTypography
                      primary={
                        <Box display="flex">
                          <Box flexGrow={1}>
                            <Typography
                              variant="h2"
                              className={classes.titleText}
                            >
                              {`${alertStatus !== null ? alertStatus : ''} ${
                                notation.notationType
                              } for ${GetCoreUserFormattedName(
                                notation.student,
                                false,
                              )}`}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="flex-end">
                            {notation.notationType === NotationType.Alert &&
                              alertStatus === 'Open' && (
                                <Chip
                                  label="Open Alert"
                                  color="default"
                                  size="small"
                                  className={classes.openAlertChip}
                                  icon={<InfoOutlinedIcon />}
                                />
                              )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          className={classes.caption}
                        >
                          {notation.createdBy &&
                            `by ${GetCoreUserFormattedName(
                              notation.createdBy,
                              false,
                            )} - `}
                          {new Date(notation.createdOn).toLocaleDateString()} at{' '}
                          {new Date(notation.createdOn).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {assignedTo && (
                            <Typography
                              variant="caption"
                              className={classes.caption}
                            >
                              {' '}
                              | Assigned To:{' '}
                              <span style={{ fontWeight: 600 }}>
                                {GetCoreUserFormattedName(assignedTo, false)}
                              </span>
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <div className={classes.detailContainerBody}>
                    <Typography
                      variant="body2"
                      component="div"
                      className={classes.detailContainer}
                      dangerouslySetInnerHTML={{ __html: notation.details }}
                    />
                  </div>
                </>
              ) : (
                <NotationDetailsLoadingSkeleton />
              )}
              <div className={classes.detailContainerBody}>
                {notation.categories && notation.categories.length > 0 && (
                  <>
                    <Typography component="h3" className={classes.heading}>
                      Categories:
                    </Typography>
                    <Box style={{ margin: '10px 0' }}>
                      <ChipContainer />
                    </Box>
                  </>
                )}

                <Box display="flex" flexDirection="row" p={0}>
                  <Box>
                    <Typography component="h3" className={classes.heading}>
                      Details:
                    </Typography>
                    <Typography variant="caption" className={classes.caption}>
                      <span>Audience: </span>
                      {audienceMapper(notation.audienceType)}

                      {notation.canStudentView && ', Visible to Student'}
                    </Typography>

                    {additionalAudience.length > 0 && (
                      <div>
                        <Typography
                          variant="caption"
                          className={classes.caption}
                        >
                          <span>Additional Audience: </span>
                          <AdditionalAudienceList
                            additionalAudience={additionalAudience}
                          />
                        </Typography>
                      </div>
                    )}
                  </Box>
                  <Box style={{ marginLeft: '15px' }}>
                    {notation.incidentDateTime && (
                      <div>
                        <Typography
                          variant="caption"
                          className={classes.caption}
                        >
                          <span style={{ fontWeight: 600 }}>
                            Incident Date:{' '}
                          </span>
                          {`${new Date(
                            notation.incidentDateTime,
                          ).toLocaleDateString()} at ${new Date(
                            notation.incidentDateTime,
                          ).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}`}
                        </Typography>
                      </div>
                    )}
                  </Box>
                </Box>
              </div>
              {canHaveComments && (
                <div className={classes.detailContainerBody}>
                  <ResponseContainer
                    notation={notation}
                    additionalAudience={additionalAudience}
                    alertStatus={alertStatus}
                    setAlertStatus={s => {
                      setAlertStatus(s);
                    }}
                    setAssignedTo={a => setAssignedTo(a)}
                    setAdditionalAudience={a => setAdditionalAudience(a)}
                  />
                </div>
              )}
            </List>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default NotationDetails;
